import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "../brand/Logo";
import { nav } from "../../content/site";
import { services } from "../../content/services";

const ServicesMenu = () => (
  <div className="invisible absolute left-1/2 top-full w-[640px] -translate-x-1/2 pt-4 opacity-0 transition-[opacity,visibility] duration-300 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
    <div className="grid grid-cols-2 gap-1 rounded-2xl border border-white/10 bg-[#141110]/95 p-3 shadow-2xl backdrop-blur-xl">
      {services.map((s) => (
        <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`}
          className="group/item flex gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-white/5">
          <span className="font-mono text-[11px] text-orange-400">{s.index}</span>
          <span>
            <span className="block text-sm font-medium text-stone-100">{s.name}</span>
            <span className="mt-0.5 block text-xs leading-relaxed text-stone-400">{s.short}</span>
          </span>
        </Link>
      ))}
    </div>
  </div>
);

const MobileMenu = ({ onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] overflow-y-auto bg-[#0C0A09] px-6 pb-10 pt-24 lg:hidden" data-testid="mobile-menu">
    <nav className="flex flex-col gap-1" aria-label="Mobile">
      {[{ label: "Home", to: "/" }, ...nav, { label: "Contact", to: "/contact" }].map((n, i) => (
        <motion.div key={n.to} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
          <Link to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\W+/g, "-")}`}
            className="flex items-center justify-between border-b border-white/10 py-4 font-display text-2xl text-stone-100">
            {n.label} <ArrowUpRight className="h-5 w-5 text-orange-400" />
          </Link>
        </motion.div>
      ))}
    </nav>
    <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-stone-400">Services</p>
    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {services.map((s) => (
        <Link key={s.slug} to={`/services/${s.slug}`} onClick={onClose}
          className="rounded-lg border border-white/10 px-4 py-3 text-sm text-stone-300">{s.name}</Link>
      ))}
    </div>
  </motion.div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setInterval(() => {
      if (window.__lenis && !window.__lenis.__tcNav) {
        window.__lenis.on("scroll", onScroll);
        window.__lenis.__tcNav = true;
      }
    }, 500);
    return () => { window.removeEventListener("scroll", onScroll); clearInterval(t); };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
    <header data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color] duration-500 ${scrolled || open ? "border-b border-white/10 bg-[#0C0A09]/[0.97] backdrop-blur-xl" : "border-b border-transparent"}`}>
      <div className={`mx-auto flex max-w-[1440px] items-center justify-between px-5 transition-[height] duration-500 sm:px-8 lg:px-12 ${scrolled ? "h-[76px]" : "h-[96px] sm:h-[104px]"}`}>
        <Link to="/" data-testid="nav-logo" className="relative z-[80]" aria-label="TechCare Technologies home"><Logo className={`w-auto transition-[height] duration-500 ${scrolled ? "h-[54px] sm:h-[62px]" : "h-[62px] sm:h-[84px]"}`} /></Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {nav.map((n) => (
            <div key={n.to} className={n.label === "Services" ? "group relative" : ""}>
              <Link to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\W+/g, "-")}`}
                className={`tc-link rounded-full px-4 py-2 text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-white ${pathname !== "/" && n.to.startsWith(pathname) && !n.to.includes("#") ? "text-orange-400" : "text-stone-300"}`}>
                {n.label}
              </Link>
              {n.label === "Services" && <ServicesMenu />}
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="nav-quote-btn"
            className="tc-btn-primary hidden h-10 items-center gap-2 rounded-full px-5 text-[13px] font-semibold sm:inline-flex">
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open} data-testid="mobile-menu-toggle"
            className="relative z-[80] grid h-10 w-10 place-items-center rounded-full border border-white/15 text-stone-100 lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </header>
    {open && <MobileMenu onClose={() => setOpen(false)} />}
    </>
  );
};
